import { useRef, useEffect, useState, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import * as Sentry from "@sentry/react";

import {
  Box,
  Grid,
  Typography,
  Avatar,
  Paper,
  useTheme,
  TableRow,
  TableCell,
  Table,
  TableHead,
  TableBody,
  TableContainer
} from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSackDollar,
  faRuler,
  faPeopleGroup,
  faGlobe,
  faEnvelope,
  faPhone
} from "@fortawesome/pro-solid-svg-icons";

import { DefaultPaper, AnimatedLogo } from "@aclymatepackages/atoms";
import {
  formatDate,
  formatDecimal,
  ucFirstLetters
} from "@aclymatepackages/formatters";
import { sumTonsCo2e, truncateText } from "@aclymatepackages/other-helpers";
import { THERMS_PER_MCF } from "@aclymatepackages/constants";
import { EmissionsPieChart } from "@aclymatepackages/modules";
import findSubcategory, {
  scopesList,
  buildSubcategoriesArray
} from "@aclymatepackages/subcategories";
import { isColorDarkTheme } from "@aclymatepackages/themes";
import { filterByDateRange } from "@aclymatepackages/date-helpers";
import { subcategories } from "@aclymatepackages/lists";

import ReportLayout from "../../layouts/ReportLayout";

import GrayPaper from "../../atoms/GrayPaper";

import ReportPage from "./ReportPage";
import EmissionsChart from "../charts/EmissionsChart";

import {
  useAccountData,
  useCachedDisplayData
} from "../../../helpers/firebase";
import {
  buildReportScopeThreeCategories,
  scopeThreeCategoryStatusLabels,
  scopeThreeSpendBasedCategories
} from "../../../helpers/components/scopeThreeCategories";
import {
  useOfficesData,
  useOfficesWithCurrentChanges
} from "../../../helpers/components/offices";
import {
  REPORT_CONTENT_HEIGHT,
  buildFacilityBoundaryRows,
  useCorsSafeImageSrc
} from "../../../helpers/components/report";
import { useRevenueYears } from "../../../helpers/hooks/companyData";
import useEmissionsContext, {
  filterEntriesOutsideClosedYears
} from "../../../helpers/contexts/emissions";
import { shippingTransportationMethods } from "../../../helpers/utils/transportationMethods";

dayjs.extend(isBetween);

const filterEmissionsBySubcategory = (emissions, subcategory) =>
  emissions.filter(({ subcategory: subcat }) => subcat === subcategory);

const sumRenewableCarbonTons = (emissions, subcategory) =>
  filterEmissionsBySubcategory(emissions, subcategory).reduce(
    (sum, emission) => sum + Number(emission.renewableCarbonTonsCo2e || 0),
    0
  );

// Stored tonsCo2e on an electricity transaction is already net of any RECs
// applied (inputs.js computes carbon from kWh minus RECs kWh before
// saving), so the raw sum of electricity tonsCo2e is already the
// Market-based figure. Location-based reflects the grid mix regardless of
// REC purchases, so it adds the RECs tons back.
//
// A closed year reads the frozen accounting snapshot, whose electricity
// entries carry renewableCarbonTonsCo2e only when the snapshot recorded it
// (helpers/utils/accountingElectricitySnapshot.js). An older snapshot's
// entries are flagged locationBasedUnavailable instead: Location is then
// reported as not available (null) rather than silently equal to Market,
// since the snapshot cannot tell "no RECs" apart from "RECs not recorded".
const calculateElectricityLocationAndMarketTons = (emissions) => {
  const electricityEmissions = filterEmissionsBySubcategory(
    emissions,
    "electricity"
  );
  const totalCategoryTons = sumTonsCo2e(electricityEmissions);
  const totalCategoryRenewableTons = sumRenewableCarbonTons(
    emissions,
    "electricity"
  );
  const locationBasedUnavailable = electricityEmissions.some(
    ({ locationBasedUnavailable: unavailable }) => unavailable === true
  );

  return {
    locationBasedTons: locationBasedUnavailable
      ? null
      : totalCategoryTons + totalCategoryRenewableTons,
    marketBasedTons: totalCategoryTons
  };
};

const LOCATION_BASED_UNAVAILABLE_TEXT = "Not available for this closed year";

// Electricity consumed and RECs applied for the Scope 2 page, from the same
// usage the facility electricity table reports: closed-year months from the
// accounting snapshot's per-month kWh (carried on its electricity entries),
// open months from the raw utility rows (transactions, recurring, office
// defaults). The aggregated month rollups in the report emissions carry no
// kWh, so there is no double count between the two. Returns null when any
// electricity in the period comes from a snapshot with no usage recorded,
// since a partial kWh figure would understate coverage.
const buildElectricityUsageSummary = ({
  emissions = [],
  openPeriodUsageSources = []
}) => {
  const electricityEmissions = filterEmissionsBySubcategory(
    emissions,
    "electricity"
  );
  if (
    electricityEmissions.some(
      ({ locationBasedUnavailable }) => locationBasedUnavailable === true
    )
  ) {
    return null;
  }

  const snapshotElectricity = electricityEmissions.filter(
    ({ source }) => source === "accounting"
  );
  const openElectricity = openPeriodUsageSources.filter(
    ({ subcategory }) =>
      subcategory === "electricity" || subcategory === "utilities"
  );
  const usageRows = [...snapshotElectricity, ...openElectricity];

  return {
    kwh: sumOfficeEmissionsProperty(usageRows, "electricKwh"),
    recsKwh: sumOfficeEmissionsProperty(usageRows, "recsKwh")
  };
};

const filterEmissionsByScope = (emissions, scope) =>
  emissions.filter(
    ({ subcategory }) => findSubcategory(subcategory).scope === scope
  );

const splitUtilityEmission = (emission) => {
  const gasTons =
    Number(emission.gasCarbonTons) ||
    Number(emission.monthlyGasCarbonTons) ||
    0;
  const electricTons =
    Number(emission.electricCarbonTons) ||
    Number(emission.monthlyElectricCarbonTons) ||
    0;

  if (!gasTons && !electricTons) {
    return [emission];
  }

  const gasRecord = gasTons
    ? [
        {
          ...emission,
          subcategory: "gas",
          tonsCo2e: gasTons,
          renewableCarbonTonsCo2e: 0
        }
      ]
    : [];
  const electricRecord = electricTons
    ? [
        {
          ...emission,
          subcategory: "electricity",
          tonsCo2e: electricTons
        }
      ]
    : [];

  return [...gasRecord, ...electricRecord];
};

const expandUtilitiesEmissions = (emissions) =>
  emissions.flatMap((emission) =>
    emission.subcategory === "utilities"
      ? splitUtilityEmission(emission)
      : [emission]
  );

// helpers/components/offices.js's useOfficesData adds a synthetic copy of each
// renewable-percentage utility emission with subcategory "renewable-energy"
// and a POSITIVE tonsCo2e equal to the emissions the renewables avoided, so
// the Offices UI can chart them. The subcategories list files
// "renewable-energy" under Scope 2, so summing a facility's Scope 2 rows as-is
// adds avoided emissions on top of real ones. The dashboard's allEmissions
// (which drives the Scope pages and the closed-year snapshot) never contains
// these rows, which is why the facility summary disagreed with the Scope 2
// page.
const AVOIDED_EMISSIONS_SUBCATEGORIES = ["renewable-energy"];

// Puts a facility's emissions on the same footing as the report's Scope pages:
// combined "utilities" rows are split into gas/electricity the same way
// ReportPages splits the dashboard emissions ("utilities" has no scope of its
// own, so unsplit it silently fell out of every facility scope row), and
// avoided-emission rows are dropped since they are not part of the inventory.
const buildFacilityInventoryEmissions = (officeEmissions = []) =>
  expandUtilitiesEmissions(officeEmissions).filter(
    ({ subcategory }) => !AVOIDED_EMISSIONS_SUBCATEGORIES.includes(subcategory)
  );

const filterEmissionsToReportPeriod = (emissions, startDate, endDate) =>
  emissions.filter(({ date }) =>
    filterByDateRange({
      filterStartDate: startDate,
      filterEndDate: endDate,
      date,
      unit: "day"
    })
  );

// Per scope: the Scope page total, the part of it the Facility Scope Emissions
// Summary attributes to facilities, and the remainder that belongs to no
// facility (vendor purchases, travel, commuting and home offices not tied to
// an office, and so on). `emissions` must be the same (utilities-expanded)
// array the Scope pages are built from. In a closed year the Scope pages read
// the frozen accounting snapshot while facility rows are live, so a negative
// unattributedTons means facility data changed after the year was closed.
const buildFacilityScopeReconciliation = ({
  offices = [],
  emissions = [],
  startDate,
  endDate
}) => {
  const facilityEmissions = offices.flatMap(({ emissions: officeEmissions }) =>
    filterEmissionsToReportPeriod(
      buildFacilityInventoryEmissions(officeEmissions),
      startDate,
      endDate
    )
  );

  return scopesList.map(({ name: scope }) => {
    const reportTons = sumTonsCo2e(filterEmissionsByScope(emissions, scope));
    const facilityTons = sumTonsCo2e(
      filterEmissionsByScope(facilityEmissions, scope)
    );

    return {
      scope,
      reportTons,
      facilityTons,
      unattributedTons: reportTons - facilityTons
    };
  });
};

// Tolerance for float noise when comparing live facility totals against the
// snapshot totals, in tCO2e.
const RECONCILIATION_TOLERANCE_TONS = 0.005;

const buildFacilityReconciliationText = ({ reconciliation, isYearLocked }) => {
  const scopeTonsList = (property) =>
    reconciliation
      .map(
        ({ scope, [property]: tons }) =>
          `Scope ${scope} ${formatDecimal(tons)}`
      )
      .join(", ");

  const baseText = `Facility rows include only emissions attributed to a facility and exclude emissions avoided through renewable energy. Of this inventory's scope totals (${scopeTonsList(
    "reportTons"
  )} tCO2e), facilities account for ${scopeTonsList(
    "facilityTons"
  )} tCO2e. The remainder is not tied to a facility and is reported on the Scope pages.`;

  if (!isYearLocked) {
    return baseText;
  }

  const lockedYearText =
    " This reporting year is closed: the Scope pages use the inventory recorded when the year was closed, while facility rows are calculated from current facility data.";

  const overstatedScopes = reconciliation.filter(
    ({ unattributedTons }) => unattributedTons < -RECONCILIATION_TOLERANCE_TONS
  );

  if (!overstatedScopes.length) {
    return `${baseText}${lockedYearText}`;
  }

  const overstatedText = overstatedScopes
    .map(
      ({ scope, unattributedTons }) =>
        `Scope ${scope} by ${formatDecimal(-unattributedTons)} tCO2e`
    )
    .join(", ");

  return `${baseText}${lockedYearText} Facility data has changed since the year was closed: facility totals exceed the closed-year inventory for ${overstatedText}.`;
};

const toAccountingYear = ({ date }) =>
  dayjs(date?.toDate ? date.toDate() : date).year();

const buildClosedYearsSet = (accountingYears) =>
  new Set((accountingYears || []).map(toAccountingYear));

const findIsReportYearLocked = (accountingYears, startDate) => {
  const reportYear = dayjs(startDate).year();
  return (accountingYears || []).some(
    ({ date }) => dayjs(date?.toDate ? date.toDate() : date).year() === reportYear
  );
};

const parseNumericValue = (value) =>
  typeof value === "number" ? value : Number(String(value).replaceAll(",", ""));

const hasNumericProperty = (emission, property, alternateProperty) => {
  const { emissionData = {}, details } = emission;
  const emissionWithData = { ...emission, ...emissionData, ...details };
  return (
    emissionWithData?.[property] != null ||
    emissionWithData?.[alternateProperty] != null
  );
};

const extractEmissionProperty = (emission, property, alternateProperty) => {
  const { emissionData = {}, details } = emission;
  const emissionWithData = { ...emission, ...emissionData, ...details };
  const emissionProperty =
    emissionWithData?.[property] ?? emissionWithData?.[alternateProperty] ?? 0;

  return parseNumericValue(emissionProperty);
};

const sumOfficeEmissionsProperty = (emissions, property, alternateProperty) =>
  emissions.reduce(
    (sum, emission) =>
      sum +
      Number(extractEmissionProperty(emission, property, alternateProperty)),
    0
  );

// mcf/ccf convert to cubic feet directly; any other unit (the "therms" default)
// converts via THERMS_PER_MCF, matching EmissionsCsvDownload.js's findGasTherms
const GAS_CUBIC_FEET_PER_UNIT = {
  mcf: 1000,
  ccf: 100
};

// null (not 0) signals "no usage data on this record" so callers can render
// a dash instead of a misleading "0 cf"/"0 kWh" for genuinely missing data
const gasCubicFeet = ({
  details = {},
  emissionData = {},
  gasUnit,
  gasUnitValue,
  fuelType
}) => {
  const { totalGasCf } = details;
  if (totalGasCf != null) {
    return parseNumericValue(totalGasCf) || 0;
  }

  // emissionData is the current, nested source of truth -- gasUnit/gasUnitValue/
  // fuelType at the top level are only a legacy fallback for transactions that
  // pre-date emissionData. An edit only ever rewrites emissionData, so a row
  // that's been edited since can have a stale, no-longer-accurate top-level
  // fuelType/gasUnit sitting next to the real, current emissionData -- reading
  // top-level first (as this used to) would apply that stale value's
  // conversion instead of the real one (PR #2192 review finding). Preferring
  // nested first, top-level only when nested is absent entirely, matches
  // EmissionsCsvDownload.js's formatCsvEmissions.
  const rawValue = emissionData.gasUnitValue ?? gasUnitValue;
  if (rawValue == null) {
    return null;
  }

  const unit = emissionData.gasUnit ?? gasUnit;
  const resolvedFuelType = emissionData.fuelType ?? fuelType;

  // Propane's raw reading is gallons, not a natural-gas-equivalent unit --
  // there's no honest cubic-feet conversion for it, so it hits the same
  // null/no-data path as a row with no usage data at all (matching how
  // wood/heating-oil rows, which never get a gasUnit at all, already render).
  // Checked via fuelType as well as gasUnit === "gallons" (matching
  // EmissionsCsvDownload.js's findGasTherms): useGasInputProps
  // (helpers/components/inputs.js) only ever persists gasUnit for
  // fuelType === "naturalGas"/"propane", so re-saving a propane transaction
  // whose editor session never wrote a nested gasUnit at all would otherwise
  // silently fall through to the therms-default conversion for that row.
  if (unit === "gallons" || resolvedFuelType === "propane") {
    return null;
  }

  const cubicFeetPerUnit = GAS_CUBIC_FEET_PER_UNIT[unit] ?? 1000 / THERMS_PER_MCF;
  return parseNumericValue(rawValue) * cubicFeetPerUnit;
};

const buildSubcategoryUsage = (subcategory, emissions) => {
  const hasKwhData = emissions.some((emission) =>
    hasNumericProperty(emission, "electricKwh", "totalKwh")
  );
  const hasGasData = emissions.some(
    (emission) => gasCubicFeet(emission) !== null
  );

  const kwhString = () =>
    `${formatDecimal(
      sumOfficeEmissionsProperty(emissions, "electricKwh", "totalKwh")
    )} kWh`;
  const gasString = () =>
    `${formatDecimal(
      emissions.reduce((sum, emission) => sum + (gasCubicFeet(emission) ?? 0), 0)
    )} cf`;

  const usageMap = {
    electricity: () => (hasKwhData ? kwhString() : "—"),
    gas: () => (hasGasData ? gasString() : "—"),
    "purchased-heat": () => (hasGasData ? gasString() : "—"),
    "upstream-leased-assets": () =>
      [hasKwhData && kwhString(), hasGasData && gasString()]
        .filter(Boolean)
        .join(" · ") || "—"
  };

  const usageFn = usageMap[subcategory];
  return usageFn ? usageFn() : "—";
};

const SHIPPING_TRANSPORT_SUBCATEGORIES = [
  "upstream-transport-distribution",
  "downstream-transportation-and-distribution"
];

const findShippingEmissions = (emissions) =>
  Array.isArray(emissions)
    ? emissions.filter(
        ({ subcategory }) =>
          subcategory === "shipping" ||
          SHIPPING_TRANSPORT_SUBCATEGORIES.includes(subcategory)
      )
    : [];

const findShippingDirection = ({
  direction,
  scopeThreeCategory,
  subcategory
}) => {
  if (direction) {
    return direction;
  }
  if (
    subcategory === "upstream-transport-distribution" ||
    scopeThreeCategory === 4
  ) {
    return "upstream";
  }
  return "downstream";
};

const buildShippingMethodRow = (label, methodEmissions) => {
  const upstreamTons = sumTonsCo2e(
    methodEmissions.filter((e) => findShippingDirection(e) === "upstream")
  );
  const downstreamTons = sumTonsCo2e(
    methodEmissions.filter((e) => findShippingDirection(e) === "downstream")
  );
  return {
    name: label,
    upstreamTons,
    downstreamTons,
    totalTons: upstreamTons + downstreamTons
  };
};

const filterOfficesByPeriod = ({ offices, startDate, endDate }) =>
  offices.filter(
    ({ endDate: officeEndDate, startDate: officeStartDate }) =>
      (officeEndDate ? dayjs(officeEndDate).isAfter(startDate) : true) &&
      (officeStartDate ? dayjs(officeStartDate).isBefore(endDate) : true)
  );

const buildReportSubcategoryDescriptions = (companyName, subcategory) => {
  const subcategoryDescriptions = {
    flights: `Flights are trips that any of ${companyName}'s employees take on a commercial airline for business purposes. This includes domestic and international flights.`,
    hotels: `Hotel stays are accommodations booked for employees or guests during business travel. This includes any lodging expenses incurred while traveling for business purposes.`,
    rides:
      "Rides refer to any transportation services used for business purposes, such as ride-sharing or taxi services. This includes trips taken by employees or guests.",
    trains:
      "Train rides refer to any rail transportation used for business purposes. This includes trips taken by employees or guests on passenger trains, such as Amtrak or regional rail",
    mileage:
      "Mileage refers to any drive in a vehicle for business purposes. This includes all miles driven by employees in their personal vehicles when on company business.",
    "company-vehicles": `Company vehicles emissions come from any vehicles owned or leased by ${companyName}'s organization that are used for business purposes. This includes cars, trucks, and vans.`,
    "rental-cars":
      "Leased or personal vehicles emissions come from any automobiles that are rented or personally owned by employees and used for business purposes. This also includes rental cars.",
    "equipment-fuel": `Equipment fuel refers to the fuel consumed by non-road equipment used in ${companyName}'s operations. This includes fuels used in construction, landscaping, or any other machinery.`,
    "employee-commuting":
      "This category includes emissions from employees commuting to and from work using personal vehicles. This includes all miles driven by employees in their personal vehicles.",
    "home-office": `This category includes emissions from employees' home offices during the time that they are working remotely for ${companyName}'s company.`,
    shipping:
      "Shipping emissions refer to the greenhouse gas emissions from the downstream and upstream transportation of goods or products by third-party carriers.",
    waste: `Waste emissions refer to the greenhouse gas emissions from the disposal and treatment of waste generated by ${companyName}'s operations. This includes solid waste, recycling, and composting.`,
    "renewable-energy":
      "Renewable energy usage includes emissions avoided through the procurement of electricity from renewable sources such as solar, wind, or hydroelectric power.",
    "spend-based": `Spend-based emissions refer to the greenhouse gas emissions associated with the purchases made by ${companyName}'s organization. This includes all goods and services purchased.`,
    refrigerants:
      "Refrigerants are chemicals used in cooling systems, such as air conditioners and refrigerators. They can have a high global warming potential (GWP) if released into the atmosphere.",
    utilities:
      "This category includes greenhouse gas emissions from utility providers that supply a combination of gas, electricity, steam, or water.",
    gas: `This category includes the greenhouse gas emissions from natural gas used in ${companyName}'s office buildings. Natural gas is commonly used for heating and cooking.`,
    electricity: `This category includes emissions from the electricity consumed by ${companyName}'s organization's facilities and provided by ${companyName}'s electric utility.`,
    "chill-water":
      "Chill water is a type of cooling fluid used in large commercial and industrial air conditioning systems. It is produced by an electrically-powered chiller and circulated throughout.",
    steam:
      "Steam is a heating medium used in many commercial and industrial applications, including heating buildings and powering equipment.",
    "purchased-heat": `Purchased heat is heat supplied to ${companyName}'s facilities by a third party, such as a landlord's central boiler or a district heating system, where ${companyName} does not own or operate the combustion equipment. Emissions are based on the fuel burned to produce the heat.`,
    "purchased-water": `Purchased water refers to the water that ${companyName}'s organization buys from a municipal or private water utility.`,
    "purchased-goods-services": `This category of spend-based emissions includes all "consumable" goods and services that are used by ${companyName}'s business, such as office supplies, furniture, and professional services.`,
    "capital-goods": `This category of spend-based emissions includes any capital good that ${companyName} invests in such as production equipment, inventory, and other equipment that's a long-term operations investment.`,
    "upstream-transport-distribution": `This category of spend-based emissions includes emissions from transportation and distribution of goods that are a part of ${companyName}'s business' supply chain.`,
    "downstream-transportation-and-distribution": `This category of spend-based emissions includes emissions from transportation and distribution of ${companyName}'s products to customers.`,
    "business-travel": `This category of spend-based emissions includes emissions from transporting, distributing ${companyName}'s goods downstream from ${companyName}'s facilities to ${companyName}'s customers.`,
    "waste-generated-in-operations": `This category of spend-based emissions includes emissions from waste generated in ${companyName}'s business' operations.`,
    events: `This category includes any emissions related to ${companyName}'s events that aren't otherwise included in activity- or spend-based emissions tagged to ${companyName}'s event, such as facilities rental.`,
    travel:
      "This category includes all emissions related to travel that are not captured in more specific subcategories such as flights, hotel stays, or rides."
  };

  return subcategoryDescriptions[subcategory];
};

const ColoredAvatar = ({ color }) => (
  <div
    style={{
      backgroundColor: color,
      height: "15px",
      width: "15px",
      borderRadius: "50%"
    }}
  />
);

const ColoredAvatarRow = ({ color, text, half }) => (
  <Grid item xs={half ? 6 : 12}>
    <Grid container spacing={1} alignItems="center" wrap="nowrap">
      <Grid item>
        <ColoredAvatar color={color} />
      </Grid>
      <Grid item>
        <Typography variant="caption">{text}</Typography>
      </Grid>
    </Grid>
  </Grid>
);

const ColoredAvatarRows = ({ rows, notHalf }) => (
  <Grid container spacing={0.5}>
    {rows.map((row, key) => (
      <ColoredAvatarRow
        half={!notHalf && rows.length > 2}
        key={`color-avatar-row-${key}`}
        {...row}
      />
    ))}
  </Grid>
);

// A "bullet"-style comparison bar for Scope 2 electricity: a full-width bar
// for Location-based tons behind, with a narrower bar for Market-based tons
// drawn in front of it. Location-based is always >= Market-based (it's
// Market-based plus any RECs tons), so Location-based can always anchor the
// 100% reference width without the Market-based bar ever overflowing it.
const LocationMarketComparisonBar = ({ locationBasedTons, marketBasedTons }) => {
  const { palette } = useTheme();

  if (locationBasedTons === null) {
    return (
      <Typography
        variant="body2"
        color="textSecondary"
        data-testid="location-based-unavailable"
      >
        Location-based electricity emissions were not recorded when this
        accounting year was closed, so they cannot be compared here.
        Market-based: {formatDecimal(marketBasedTons)} tCO2e.
      </Typography>
    );
  }

  const marketBarWidthPercent = locationBasedTons
    ? Math.min((marketBasedTons / locationBasedTons) * 100, 100)
    : 0;

  return (
    <Box>
      <Grid
        container
        spacing={2}
        alignItems="center"
        style={{ marginBottom: "8px" }}
      >
        <Grid item>
          <Grid container spacing={1} alignItems="center" wrap="nowrap">
            <Grid item>
              <ColoredAvatar color={palette.backgroundGray.dark} />
            </Grid>
            <Grid item>
              <Typography variant="caption">Location-Based</Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container spacing={1} alignItems="center" wrap="nowrap">
            <Grid item>
              <ColoredAvatar color={palette.primary.main} />
            </Grid>
            <Grid item>
              <Typography variant="caption">Market-Based</Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Box
        style={{
          position: "relative",
          height: "28px",
          borderRadius: "4px",
          overflow: "hidden",
          backgroundColor: palette.backgroundGray.dark
        }}
      >
        <Box
          data-testid="market-based-comparison-bar"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            width: `${marketBarWidthPercent}%`,
            backgroundColor: palette.primary.main
          }}
        />
      </Box>
      <Grid
        container
        justifyContent="space-between"
        style={{ marginTop: "4px" }}
      >
        <Grid item>
          <Typography variant="caption" color="textSecondary">
            {formatDecimal(locationBasedTons)} tCO2e
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="caption" color="textSecondary">
            {formatDecimal(marketBasedTons)} tCO2e
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

const PageTitle = ({ children }) => (
  <Typography variant="h3" align="center" gutterBottom color="textPrimary">
    {children}
  </Typography>
);

const PageHeaderText = ({ title, style = {}, children }) => (
  <div style={style}>
    <PageTitle>{title}</PageTitle>
    <Typography variant="body2" align="center" color="textSecondary">
      {children}
    </Typography>
  </div>
);

const TitlePage = ({ startDate, endDate }) => {
  const [{ branding, name }] = useAccountData();
  const { logoUrl } = branding || {};
  const logoSrc = useCorsSafeImageSrc(logoUrl);

  return (
    <Box style={{ height: "100%" }} display="flex" alignItems="center">
      <Grid container direction="column" spacing={1}>
        <Grid item>
          <Box display="flex" justifyContent="center">
            {logoSrc && (
              <img
                src={logoSrc}
                alt="company logo"
                style={{ width: "400px", margin: "auto" }}
              />
            )}
          </Box>
        </Grid>
        <Grid item>
          <Typography variant="h3" align="center" color="textPrimary">
            {name} Carbon Emissions Report
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="h5" align="center" color="textSecondary">
            Reporting period: {formatDate(startDate)} -{" "}
            {formatDate(dayjs(endDate).isAfter(dayjs()) ? new Date() : endDate)}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

const EmissionsSummaryPage = ({
  emissions,
  startDate,
  endDate,
  totalReportPeriodTons,
  offices,
  graphPeriod
}) => {
  const { palette } = useTheme();
  const { employees } = useEmissionsContext();

  const [revenueYears] = useRevenueYears();

  const [{ branding, employeeCount }] = useAccountData();
  const { companyStatement } = branding || {};

  const coloredAvatarRows = scopesList.map(({ name, color }) => {
    const scopeTonsSum = sumTonsCo2e(filterEmissionsByScope(emissions, name));

    return {
      color: palette[color].main,
      text: `Total Scope ${name} Emissions: ${formatDecimal(
        scopeTonsSum
      )} tons CO2e${!scopeTonsSum ? " *" : ""}`,
      scopeTonsSum
    };
  });

  const findRevenueEmissionIntensity = () => {
    if (!revenueYears) {
      return [];
    }
    const revenueYear = revenueYears.find(
      ({ year }) => year === dayjs(startDate).year()
    );

    if (!revenueYear) {
      return [];
    }

    const { revenue } = revenueYear;
    if (!revenue) {
      return [];
    }

    const buildRevenueIntensityEntry = (divisor, label) => {
      const revenueNearestUnit = Math.round(revenue / divisor);
      if (!revenueNearestUnit) {
        return null;
      }
      const emissionsPerUnit = totalReportPeriodTons / revenueNearestUnit;
      return {
        icon: faSackDollar,
        text: `${formatDecimal(emissionsPerUnit)} tCO2e/ ${label} revenue`
      };
    };

    const revenueIntensityEntry =
      revenue < 1000000
        ? buildRevenueIntensityEntry(100000, "$100K") ||
          buildRevenueIntensityEntry(1000, "$1K")
        : buildRevenueIntensityEntry(1000000, "$1M");

    return revenueIntensityEntry ? [revenueIntensityEntry] : [];
  };

  const findEmployeeEmissionIntensity = () => {
    const employeesThisPeriod = employees.filter(
      ({ startDate: employeeStartDate, endDate: employeeEndDate }) =>
        (employeeStartDate
          ? dayjs(employeeStartDate).isBefore(endDate)
          : true) &&
        (employeeEndDate ? dayjs(employeeEndDate).isAfter(startDate) : true)
    );

    const numberOfEmployees = employeeCount || employeesThisPeriod.length;

    const emissionsPerEmployee = totalReportPeriodTons / numberOfEmployees;

    return {
      icon: faPeopleGroup,
      text: `${formatDecimal(emissionsPerEmployee)} tCO2e/ Employee`
    };
  };

  const findAreaEmissionIntensity = () => {
    const employeeHomeOffices = employees
      .map(({ commuteSchedules = [] }) => {
        if (!commuteSchedules.length) {
          return null;
        }
        return commuteSchedules.map(({ home }) => {
          if (!home) {
            return null;
          }
          const { homeOfficeArea } = home;
          return homeOfficeArea;
        });
      })
      .flat()
      .filter((area) => !!area);

    const totalHomeOfficeArea = employeeHomeOffices.reduce(
      (sum, area) => sum + Number(area || 0),
      0
    );
    const totalOfficeArea = offices.reduce(
      (sum, { sqFootage }) => sum + Number(sqFootage || 0),
      0
    );

    const totalSqFootage = totalOfficeArea + totalHomeOfficeArea;

    if (!totalSqFootage) {
      return [];
    }

    const carbonTonsPerSqFt = totalReportPeriodTons / totalSqFootage;

    return [
      {
        icon: faRuler,
        text: `${formatDecimal(
          carbonTonsPerSqFt,
          4
        )} tCO2e/ sq. ft. of user occupied space`
      }
    ];
  };

  const carbonIntensities = [
    ...findRevenueEmissionIntensity(),
    findEmployeeEmissionIntensity(),
    ...findAreaEmissionIntensity()
  ];

  return (
    <Grid container spacing={2} direction="column">
      <Grid item>
        <PageHeaderText title="Company Statement">
          {companyStatement}
        </PageHeaderText>
      </Grid>
      <Grid item>
        <Typography variant="h5" align="center" gutterBottom>
          Emissions Summary
        </Typography>
        <GrayPaper>
          <Grid container spacing={2} direction="column">
            <Grid item container spacing={2} wrap="nowrap" alignItems="stretch">
              <Grid item xs={2.5}>
                <DefaultPaper>
                  <Grid container direction="column" spacing={1}>
                    {scopesList.map(({ name, color }, idx) => (
                      <ColoredAvatarRow
                        color={palette[color].main}
                        key={`scope-row-${idx}`}
                        text={`Scope ${name}`}
                      />
                    ))}
                  </Grid>
                </DefaultPaper>
              </Grid>
              <Grid item xs={9.5}>
                <DefaultPaper style={{ height: "100%" }}>
                  <Grid container spacing={4} align="center" wrap="nowrap">
                    <Grid item>
                      <Typography variant="h6" align="left">
                        Total Emissions:
                      </Typography>
                      <Typography variant="h4" noWrap>
                        {formatDecimal(sumTonsCo2e(emissions))} tons CO2e
                      </Typography>
                    </Grid>
                    <Grid item>
                      <ColoredAvatarRows rows={coloredAvatarRows} notHalf />
                    </Grid>
                  </Grid>
                </DefaultPaper>
              </Grid>
            </Grid>
            <Grid item>
              <DefaultPaper>
                <Grid container spacing={4}>
                  <Grid item xs={4}>
                    <Typography variant="h5" align="center" gutterBottom>
                      Scope Breakdown
                    </Typography>
                    <EmissionsPieChart
                      dataArray={emissions}
                      viewMode="scopes"
                      aspect={1}
                    />
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="h5" align="center" gutterBottom>
                      Scope Emissions By{" "}
                      {graphPeriod === "year" ? "Quarter" : "Month"}
                    </Typography>
                    <EmissionsChart
                      type="bar"
                      dataArray={emissions}
                      viewMode="scopes"
                      showTooltip={false}
                      aspect={2}
                    />
                  </Grid>
                </Grid>
              </DefaultPaper>
            </Grid>
            <Grid item>
              <DefaultPaper>
                <Grid container spacing={2}>
                  <Grid item>
                    <Typography variant="h6">Emission Intensities: </Typography>
                  </Grid>
                  <Grid item>
                    <Grid container spacing={1} direction="column">
                      {carbonIntensities.map(({ icon, text }, idx) => (
                        <Grid
                          item
                          container
                          spacing={2}
                          key={`intensity-row-${idx}`}
                          alignItems="center"
                        >
                          <Grid item>
                            <Avatar
                              style={{
                                backgroundColor: palette.secondary.main,
                                color: "white",
                                height: "30px",
                                width: "30px"
                              }}
                            >
                              <FontAwesomeIcon icon={icon} size="xs" />
                            </Avatar>
                          </Grid>
                          <Grid item>
                            <Typography variant="body2">{text}</Typography>
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              </DefaultPaper>
            </Grid>
          </Grid>
        </GrayPaper>
      </Grid>
      {coloredAvatarRows.some(({ scopeTonsSum }) => !scopeTonsSum) && (
        <Grid item>
          <Typography variant="caption" color="textSecondary">
            *This report doesn&apos;t include detailed emissions data for this scope.
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

const ScopeCategoryDescriptionRow = ({ name, subcategory, companyName }) => {
  return (
    <Typography
      variant="caption"
      display="block"
      color="textSecondary"
      gutterBottom
      style={{ lineHeight: 1.6 }}
    >
      <strong>{name}:</strong>{" "}
      {buildReportSubcategoryDescriptions(companyName, subcategory)}
    </Typography>
  );
};

const buildCategoriesListRowsArray = (title, categories, companyName) => {
  if (!categories.length) {
    return [];
  }

  const [firstCategory, ...otherCategories] = categories;
  const titleRow = (
    <div style={{ marginTop: "8px" }}>
      <Typography variant="h6" color="textPrimary" gutterBottom>
        {title}
      </Typography>
      <ScopeCategoryDescriptionRow
        companyName={companyName}
        {...firstCategory}
      />
    </div>
  );

  if (!otherCategories.length) {
    return [titleRow];
  }

  return [
    titleRow,
    ...otherCategories.map((category) => (
      <ScopeCategoryDescriptionRow
        key={category.subcategory || category.name}
        companyName={companyName}
        {...category}
      />
    ))
  ];
};

const ElectricityRecsSummary = ({ kwh, recsKwh }) => (
  <Typography
    variant="body2"
    color="textSecondary"
    style={{ marginTop: "8px" }}
    data-testid="electricity-recs-summary"
  >
    Renewable energy certificates applied: {formatDecimal(recsKwh)} kWh
    {kwh ? ` of ${formatDecimal(kwh)} kWh consumed` : ""}
  </Typography>
);

const buildScopeDisplayBlocks = ({
  name: scope,
  emissions,
  companyName,
  electricityUsage = null
}) => {
  const scopeDescriptions = [
    `Scope 1 emissions are derived directly from fossil fuels that ${companyName} burns. This includes emissions from company-owned vehicles, equipment, and gas utilities for heating and cooking in company-occupied facilities and any other direct emissions from its operations.`,
    `Scope 2 emissions are indirect emissions from the generation of purchased energy that ${companyName} consumes: electricity, steam, heat and cooling. This includes electricity from its electric utility and heat or cooling supplied by a third party, such as a landlord's central boiler or a district energy system.`,
    `Scope 3 emissions are indirect from your ${companyName}’s purchases and any other emissions that aren't directly derived from fossils that it consumes directly. This includes emissions from its supply chain, employee commuting, business travel, waste disposal, and any other indirect emissions.`
  ];

  // const allScopeSubcategories = subcategories
  //   .filter(
  //     ({ subcategory }) =>
  //       subcategory !== "travel" && subcategory !== "renewable-energy"
  //   )
  //   .filter((subcategory) => subcategory.scope === scope);

  const scopeEmissionSubcategories = buildSubcategoriesArray(emissions);

  const hasElectricity = scopeEmissionSubcategories.some(
    ({ subcategory }) => subcategory === "electricity"
  );
  const electricityLocationAndMarketTons = hasElectricity
    ? calculateElectricityLocationAndMarketTons(emissions)
    : null;

  const coloredAvatarRows = scopeEmissionSubcategories
    .flatMap(({ color, name, subcategory }) => {
      const totalCategoryTons = sumTonsCo2e(
        filterEmissionsBySubcategory(emissions, subcategory)
      );

      if (subcategory === "electricity") {
        const { locationBasedTons, marketBasedTons } =
          electricityLocationAndMarketTons;

        return [
          {
            color,
            text:
              locationBasedTons === null
                ? `${name} (Location): ${LOCATION_BASED_UNAVAILABLE_TEXT}`
                : `${name} (Location): ${formatDecimal(
                    locationBasedTons
                  )} tCO2e`,
            // An unavailable (null) Location figure sorts to the top, not
            // the bottom -- a real but unknown figure could be large, and
            // "not available" is worth surfacing prominently rather than
            // burying it under small unrelated categories.
            totalCategoryTons: locationBasedTons ?? Number.POSITIVE_INFINITY
          },
          {
            color,
            text: `${name} (Market): ${formatDecimal(marketBasedTons)} tCO2e`,
            totalCategoryTons: marketBasedTons
          }
        ];
      }

      return {
        color,
        text: `${name}: ${formatDecimal(totalCategoryTons)} tCO2e`,
        totalCategoryTons
      };
    })
    .sort((a, b) => b.totalCategoryTons - a.totalCategoryTons);

  // const excludedSubcategories = allScopeSubcategories.filter(
  //   ({ name }) =>
  //     !scopeEmissionSubcategories.find(
  //       ({ name: subcatName }) => subcatName === name
  //     )
  // );
  const totalScopeEmissions = sumTonsCo2e(emissions);

  // A fully-offset electricity month can legitimately have a zero net
  // (Market-based) total while still carrying real (Location-based)
  // activity -- gate on whether any row actually has tonnage to show,
  // not solely on the net sum, or the whole scope disappears.
  // Likewise a closed year whose snapshot predates Location-based data:
  // the section must still render to say Location is not available, rather
  // than vanish and read as "no Scope 2 activity".
  const hasDisplayableEmissions =
    electricityLocationAndMarketTons?.locationBasedTons === null ||
    coloredAvatarRows.some(({ totalCategoryTons }) => totalCategoryTons > 0);

  // Only when the Location figure above actually reflects the RECs. An open
  // year read from the legacy all-aggregated-transactions cache (admin view,
  // or a company not on the rollups-v2 read) has no renewableCarbonTonsCo2e
  // on its electricity rows, so Location == Market there; printing "RECs
  // applied" next to that would put the contradiction this line exists to
  // remove back on the page.
  const showRecsSummary =
    electricityUsage?.recsKwh > 0 &&
    electricityLocationAndMarketTons?.locationBasedTons !== null &&
    electricityLocationAndMarketTons?.locationBasedTons >
      electricityLocationAndMarketTons?.marketBasedTons;

  if (!totalScopeEmissions && !hasDisplayableEmissions) {
    return null;
  }

  return {
    blocks: [
      <PageHeaderText
        key="scope-header"
        title={`Scope ${scope} Emissions`}
        style={{ marginBottom: "32px" }}
      >
        {scopeDescriptions[scope - 1]}
      </PageHeaderText>,
      <GrayPaper key="scope-summary" style={{ marginBottom: "32px" }}>
        <Grid container spacing={2} direction="column">
          <Grid item>
            <DefaultPaper style={{ height: "100%" }}>
              <Typography variant="h6">
                Total Scope {scope} Emissions
                {hasElectricity ? " (Market-Based)" : ""}:{" "}
                <strong>{formatDecimal(totalScopeEmissions)} tons CO2e</strong>
              </Typography>
              <ColoredAvatarRows rows={coloredAvatarRows} />
            </DefaultPaper>
          </Grid>
          {electricityLocationAndMarketTons && (
            <Grid item>
              <DefaultPaper style={{ height: "100%" }}>
                <Typography variant="h6" gutterBottom>
                  Electricity: Location-Based vs. Market-Based
                </Typography>
                <LocationMarketComparisonBar
                  {...electricityLocationAndMarketTons}
                />
                {showRecsSummary && (
                  <ElectricityRecsSummary {...electricityUsage} />
                )}
              </DefaultPaper>
            </Grid>
          )}
          <Grid item>
            <DefaultPaper style={{ height: "265px" }}>
              <Typography variant="h6" align="center" gutterBottom>
                Scope {scope} Emissions by Category
              </Typography>
              <EmissionsChart
                dataArray={emissions}
                type="bar"
                showTooltip={false}
              />
            </DefaultPaper>
          </Grid>
        </Grid>
      </GrayPaper>,
      ...buildCategoriesListRowsArray(
        `Included Categories`,
        scopeEmissionSubcategories,
        companyName
      )
      //we're commenting this out until we fix the fact that scope three categories aren't assigend properly for activity based vendor emissions
      // ...buildCategoriesListRowsArray(
      //   `Excluded Categories`,
      //   excludedSubcategories,
      //   companyName
      // ),
    ]
  };
};

const OffsetCard = ({
  name,
  shortName,
  image,
  portfolioImage,
  portfolioName,
  images = [],
  setRefElement
}) => (
  <Grid item xs={4}>
    <Paper ref={setRefElement} sx={{ height: "100%", padding: 2 }}>
      <Grid container spacing={2} direction="column">
        <Grid item>
          <Box
            style={{
              height: "125px",
              backgroundImage: `url('${image || portfolioImage || images[0]}')`,
              backgroundSize: "cover",
              backgroundPosition: "center center",
              borderRadius: "8px"
            }}
            p={1}
            position="relative"
            display="flex"
            justifyContent="flex-end"
          />
        </Grid>
        <Grid item>
          <Typography variant="h6">
            {truncateText(shortName || name || portfolioName, 40)}
            {portfolioName && " Portfolio"}
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  </Grid>
);

// Offset docs identify their project differently by purchase vintage: portfolio
// purchases carry portfolioId, older per-project purchases carry offset.offsetSlug,
// and the oldest carry only a project name. purchaseSlug is a per-purchase receipt
// id shared across the projects in one order, so it never identifies a project.
const buildOffsetProjectKey = ({ portfolioId, offset = {}, name, id }) =>
  portfolioId || offset.offsetSlug || name || id;

const dedupeOffsetsByProject = (offsets) =>
  offsets.filter((offset, idx) => {
    const projectKey = buildOffsetProjectKey(offset);

    if (!projectKey) {
      return true;
    }

    return (
      offsets.findIndex(
        (candidate) => buildOffsetProjectKey(candidate) === projectKey
      ) === idx
    );
  });

// Most offsets docs predate the attributionYear field and have neither it nor
// badgeYears set, which would otherwise make them invisible to every report year.
// Fall back to deriving the same fiscal year from the purchase date that
// findDefaultAttributionYear (functions/utils/webhooks/stripe.js) uses when it does
// set the field, so pre- and post-field purchases attribute consistently.
const findAttributionYear = ({ attributionYear, date }) => {
  if (typeof attributionYear === "number") {
    return attributionYear;
  }

  if (!date) {
    return null;
  }

  const purchaseDate = dayjs(date?.toDate ? date.toDate() : date);
  return purchaseDate.month() < 3
    ? purchaseDate.year() - 1
    : purchaseDate.year();
};

const useOffsetsPages = (graphPeriod, year) => {
  const [offsets] = useCachedDisplayData("offsets");

  const buildDisplayOffsetsArray = useCallback(() => {
    const filteredOffsets = offsets.filter((offsetDoc) => {
      const { badgeYears = [] } = offsetDoc;

      return (
        findAttributionYear(offsetDoc) === year ||
        badgeYears.find((badgeYear) => badgeYear.year === year)
      );
    });

    return dedupeOffsetsByProject(filteredOffsets);
  }, [offsets, year]);

  const displayOffsets = useMemo(
    () => buildDisplayOffsetsArray(),
    [buildDisplayOffsetsArray]
  );

  const [offsetPages, setOffsetPages] = useState([]);

  const projectCardRef = useRef([]);
  const setRefElement = (idx) => (element) => {
    projectCardRef.current[idx] = element;
  };

  useEffect(() => {
    if (displayOffsets.length && !offsetPages.length) {
      setOffsetPages([displayOffsets]);
    }
  }, [displayOffsets, offsetPages.length]);

  useEffect(() => {
    const calcAndSetOffsetPages = () => {
      const CONTENT_HEIGHT = 700;

      const cardHeight = projectCardRef.current[0]?.offsetHeight;
      const numDisplayOffsets = projectCardRef.current.length;
      const numRows = Math.floor(numDisplayOffsets / 3);

      if (cardHeight * numRows < CONTENT_HEIGHT) {
        return setOffsetPages([displayOffsets]);
      }

      const numPages = Math.ceil((cardHeight * numRows) / CONTENT_HEIGHT);
      const rowsPerPage = Math.floor(CONTENT_HEIGHT / cardHeight);

      const pagesArray = [...new Array(numPages)].map((_, idx) =>
        [...displayOffsets].slice(
          idx * rowsPerPage * 3,
          (idx + rowsPerPage) * 3
        )
      );

      return setOffsetPages(pagesArray);
    };

    if (projectCardRef.current.length) {
      calcAndSetOffsetPages();
    }
  }, [displayOffsets, projectCardRef.current.length]);

  if (!displayOffsets.length || graphPeriod === "quarter") {
    return [];
  }

  return offsetPages.map((offsetsArray, idx) => ({
    content: (
      <Grid container direction="column" spacing={2}>
        {!idx && (
          <>
            <Grid item>
              <PageHeaderText title="Offset Projects">
                Carbon offsets represent verified reductions or removals of
                greenhouse gas emissions achieved through projects such as
                renewable energy, reforestation, and methane capture.
                Purchasing offsets allows a company to compensate for emissions
                it cannot yet eliminate directly, funding projects that reduce
                or remove an equivalent amount of carbon from the atmosphere.
                The projects below were purchased during this reporting
                period.
              </PageHeaderText>
            </Grid>
          </>
        )}
        <Grid item>
          <GrayPaper>
            <Grid
              container
              spacing={2}
              alignItems="stretch"
              justifyContent="center"
            >
              {offsetsArray.map((offset, idx) => (
                <OffsetCard
                  setRefElement={setRefElement(idx)}
                  {...offset}
                  key={`offset-card-i${idx}`}
                />
              ))}
            </Grid>
          </GrayPaper>
        </Grid>
      </Grid>
    )
  }));
};

const BrandedTableCell = ({ children, ...props }) => {
  const { palette } = useTheme();

  return (
    <TableCell
      {...props}
      style={{ border: `solid thin ${palette.backgroundGray.main}` }}
    >
      {children}
    </TableCell>
  );
};

const BrandedReportTable = ({ columns, rows, RowComponent, setRefElement }) => {
  const [{ branding }] = useAccountData();
  const { primaryColor } = branding || {};
  const { palette } = useTheme();

  const headerColor = primaryColor || palette.primary.main;

  return (
    <TableContainer ref={(element) => setRefElement && setRefElement(element)}>
      <Table>
        <TableHead
          style={{
            backgroundColor: headerColor,
            color: isColorDarkTheme(headerColor) ? "white" : "inherit"
          }}
        >
          {columns.map((column, idx) => (
            <TableCell
              key={`table-head-cell-${idx}`}
              style={{
                color: "inherit",
                border: `solid thin white`
              }}
            >
              <strong>{column}</strong>
            </TableCell>
          ))}
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <RowComponent {...row} key={`table-row-${idx}`} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const useWrappedTableRows = ({
  rows,
  columns,
  titleBlockHeight,
  title,
  headerText,
  RowComponent,
  isLoading
}) => {
  const TITLE_BLOCK_HEIGHT = titleBlockHeight || 82;

  const [tableRows, setTableRows] = useState([[]]);
  const blocksRef = useRef([]);

  const setRefElement = (idx) => (element) => {
    blocksRef.current[idx] = element;
  };

  // `rows` can be a non-empty but not-yet-real placeholder (e.g. one row per
  // office with every number defaulting to 0 while the actual emissions
  // calculation is still loading) -- latching onto that snapshot before the
  // real data arrives would freeze the table on it forever, since this only
  // ever fires while tableRows is still empty. Callers whose `rows` can be
  // non-empty before their data is actually ready must pass `isLoading` so
  // this waits for the real value instead of the first non-empty one.
  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (!tableRows[0].length && rows.length) {
      return setTableRows([rows]);
    }
  }, [setTableRows, rows, tableRows, isLoading]);

  useEffect(() => {
    const buildSplitTableData = () => {
      const heights = blocksRef.current.map((ref) => ref?.offsetHeight);

      const tooTallTableIdx = heights.findIndex(
        (height) => height > REPORT_CONTENT_HEIGHT - TITLE_BLOCK_HEIGHT
      );

      if (tooTallTableIdx === -1) {
        return;
      }

      //this is a super complicated mutable function that was written with the help of Chat GPT
      //https://chatgpt.com/share/e/6801ee0a-c3bc-8000-a80f-12ca6a382bea
      //basically it iteratively shifts the last row of the table that is too tall to the next table while still maintaining the order of the rows
      const shiftArray = (array) => {
        let shifted = false;
        let skipIndex = -1;

        const newArrays = array.reduce((acc, current, index, original) => {
          if (index === skipIndex) {
            return acc;
          }

          if (shifted || index !== tooTallTableIdx) {
            return [...acc, current];
          }

          const clone = [...current];
          const lastRow = clone.pop();
          shifted = true;

          acc.push(clone);

          if (original[index + 1]) {
            acc.push([lastRow, ...original[index + 1]]);
            skipIndex = index + 1;
          } else {
            acc.push([lastRow]);
          }

          return acc;
        }, []);

        return newArrays.filter((array) => array.length > 0);
      };

      return setTableRows((tableRows) => shiftArray(tableRows));
    };

    let timeout;

    if (blocksRef.current.length) {
      timeout = setTimeout(() => buildSplitTableData(), 300);
    }

    return () => clearTimeout(timeout);
  }, [
    blocksRef.current.length,
    tableRows,
    titleBlockHeight,
    TITLE_BLOCK_HEIGHT
  ]);

  const tableRowProps = {
    columns,
    RowComponent
  };

  return tableRows.map((tableRows, idx) => ({
    content: idx ? (
      <BrandedReportTable
        {...tableRowProps}
        rows={tableRows}
        key={`table-${idx}`}
        setRefElement={setRefElement(idx)}
      />
    ) : (
      <Grid container spacing={2} direction="column" key={`table-${idx}`}>
        <Grid item>
          <PageHeaderText title={title}>{headerText}</PageHeaderText>
        </Grid>
        <Grid item>
          <BrandedReportTable
            {...tableRowProps}
            rows={tableRows}
            setRefElement={setRefElement(idx)}
          />
        </Grid>
      </Grid>
    )
  }));
};

const ScopeThreeCategoryRow = ({ name, status, isCalculated, totalTons }) => (
  <TableRow>
    <BrandedTableCell>{name}</BrandedTableCell>
    <BrandedTableCell>{status}</BrandedTableCell>
    <BrandedTableCell>
      {isCalculated ? formatDecimal(totalTons) : ""}
    </BrandedTableCell>
  </TableRow>
);

const useScopeThreeCategoriesTables = (emissions) => {
  const scopeThreeEmissions = filterEmissionsByScope(emissions, 3);

  const formattedScopeThreeEmissions = scopeThreeEmissions.map((emission) => {
    const { subcategory } = emission;
    if (
      scopeThreeSpendBasedCategories.find(
        ({ subcategory: catSubcategory }) => catSubcategory === subcategory
      )
    ) {
      return emission;
    }

    const { scopeThreeCategory } = subcategories.find(
      ({ subcategory: catSubcategory }) => catSubcategory === subcategory
    ) || { scopeThreeCategory: null };

    const { subcategory: scopeThreeCategorySubcategory } =
      scopeThreeSpendBasedCategories.find(
        (category) => category.scopeThreeCategory === scopeThreeCategory
      ) || { subcategory: "Other" };

    return { ...emission, subcategory: scopeThreeCategorySubcategory };
  });

  const emissionsByCategory = buildReportScopeThreeCategories().map(
    (category) => {
      const emissionsBySubcategory = category.isSupported
        ? formattedScopeThreeEmissions.filter(
            ({ subcategory }) => subcategory === category.subcategory
          )
        : [];

      // A category with no emissions records at all is one we have no input data for, which is
      // reported differently from a category that was calculated and came to zero.
      const isCalculated = !!emissionsBySubcategory.length;

      return {
        ...category,
        isCalculated,
        status: isCalculated
          ? scopeThreeCategoryStatusLabels.calculated
          : scopeThreeCategoryStatusLabels.notCalculated,
        totalTons: sumTonsCo2e(emissionsBySubcategory)
      };
    }
  );

  return useWrappedTableRows({
    rows: emissionsByCategory,
    columns: ["Scope 3 Category", "Status", "Tonnes CO2e Total"],
    title: "Scope 3 Breakdown",
    RowComponent: ScopeThreeCategoryRow,
    headerText:
      "This table lists all fifteen Scope 3 categories defined by the GHG Protocol Corporate Value Chain (Scope 3) Standard. Categories marked \u201cCalculated\u201d were calculated from data provided by the company and its vendors. Categories marked \u201cNot calculated\u201d were not evaluated for this reporting period and are excluded from the totals in this report; they should not be read as zero."
  });
};

const ShippingBreakdownRow = ({
  name,
  upstreamTons,
  downstreamTons,
  totalTons,
  isTotal
}) => (
  <TableRow>
    <BrandedTableCell>
      {isTotal ? <strong>{name}</strong> : name}
    </BrandedTableCell>
    <BrandedTableCell>
      {isTotal ? (
        <strong>{formatDecimal(upstreamTons)}</strong>
      ) : (
        formatDecimal(upstreamTons)
      )}
    </BrandedTableCell>
    <BrandedTableCell>
      {isTotal ? (
        <strong>{formatDecimal(downstreamTons)}</strong>
      ) : (
        formatDecimal(downstreamTons)
      )}
    </BrandedTableCell>
    <BrandedTableCell>
      {isTotal ? (
        <strong>{formatDecimal(totalTons)}</strong>
      ) : (
        formatDecimal(totalTons)
      )}
    </BrandedTableCell>
  </TableRow>
);

const shippingTransportationMethodOf = ({
  transportationMethod,
  emissionData = {}
}) => transportationMethod ?? emissionData.transportationMethod;

// exported for unit testing: known-mode tonnage (from the scoped shipping
// transaction query) can legitimately be less than the aggregated total
// (unmoded shipping rolls into "Unspecified"), but should never exceed it —
// callers should treat a negative residual as a reconciliation signal, not
// just silently clamp it away
const computeUnspecifiedShippingTons = ({
  totalUpstreamTons,
  totalDownstreamTons,
  knownUpstreamTons,
  knownDownstreamTons
}) => {
  const upstreamResidual = totalUpstreamTons - knownUpstreamTons;
  const downstreamResidual = totalDownstreamTons - knownDownstreamTons;
  const unspecifiedUpstreamTons = Math.max(upstreamResidual, 0);
  const unspecifiedDownstreamTons = Math.max(downstreamResidual, 0);

  return {
    unspecifiedUpstreamTons,
    unspecifiedDownstreamTons,
    unspecifiedTotalTons: unspecifiedUpstreamTons + unspecifiedDownstreamTons,
    hasReconciliationMismatch: upstreamResidual < 0 || downstreamResidual < 0
  };
};

const useShippingBreakdownTable = (emissions, { startDate, endDate }) => {
  const [shippingModeTransactions = [], shippingModeTransactionsLoading] =
    useCachedDisplayData("transactions", [["subcategory", "==", "shipping"]]);

  const shippingEmissions = findShippingEmissions(emissions);

  const totalUpstreamTons = sumTonsCo2e(
    shippingEmissions.filter((e) => findShippingDirection(e) === "upstream")
  );
  const totalDownstreamTons = sumTonsCo2e(
    shippingEmissions.filter((e) => findShippingDirection(e) === "downstream")
  );

  const periodModeTransactions = shippingModeTransactions.filter(({ date }) =>
    filterByDateRange({
      filterStartDate: startDate,
      filterEndDate: endDate,
      date,
      unit: "day"
    })
  );

  const methodRows = shippingTransportationMethods
    .map(({ value, label }) => {
      const methodEmissions = periodModeTransactions.filter(
        (transaction) => shippingTransportationMethodOf(transaction) === value
      );
      return buildShippingMethodRow(label, methodEmissions);
    })
    .filter(({ totalTons }) => totalTons !== 0);

  const knownUpstreamTons = methodRows.reduce(
    (sum, { upstreamTons }) => sum + upstreamTons,
    0
  );
  const knownDownstreamTons = methodRows.reduce(
    (sum, { downstreamTons }) => sum + downstreamTons,
    0
  );

  const {
    unspecifiedUpstreamTons,
    unspecifiedDownstreamTons,
    unspecifiedTotalTons,
    hasReconciliationMismatch
  } = computeUnspecifiedShippingTons({
    totalUpstreamTons,
    totalDownstreamTons,
    knownUpstreamTons,
    knownDownstreamTons
  });

  useEffect(() => {
    if (!hasReconciliationMismatch) {
      return;
    }

    Sentry.withScope((scope) => {
      scope.setTag("feature", "shipping-breakdown-report");
      scope.setExtra("startDate", startDate);
      scope.setExtra("endDate", endDate);
      scope.setExtra("knownUpstreamTons", knownUpstreamTons);
      scope.setExtra("totalUpstreamTons", totalUpstreamTons);
      scope.setExtra("knownDownstreamTons", knownDownstreamTons);
      scope.setExtra("totalDownstreamTons", totalDownstreamTons);
      Sentry.captureMessage(
        "Shipping breakdown: known-mode tonnage exceeds aggregated total"
      );
    });
  }, [
    hasReconciliationMismatch,
    startDate,
    endDate,
    knownUpstreamTons,
    totalUpstreamTons,
    knownDownstreamTons,
    totalDownstreamTons
  ]);

  const unspecifiedRows = unspecifiedTotalTons
    ? [
        {
          name: "Unspecified*",
          upstreamTons: unspecifiedUpstreamTons,
          downstreamTons: unspecifiedDownstreamTons,
          totalTons: unspecifiedTotalTons
        }
      ]
    : [];

  const sortedRows = [...methodRows, ...unspecifiedRows].sort(
    (a, b) => b.totalTons - a.totalTons
  );

  const rows = [
    ...sortedRows,
    {
      name: "Total",
      upstreamTons: totalUpstreamTons,
      downstreamTons: totalDownstreamTons,
      totalTons: totalUpstreamTons + totalDownstreamTons,
      isTotal: true
    }
  ];

  const pages = useWrappedTableRows({
    rows: shippingEmissions.length ? rows : [],
    columns: [
      "Mode",
      "Upstream (tCO2e)",
      "Downstream (tCO2e)",
      "Total (tCO2e)"
    ],
    title: "Shipping Emissions Breakdown",
    RowComponent: ShippingBreakdownRow,
    headerText:
      "This table shows shipping emissions broken down by transportation mode and direction. Upstream shipping covers supply chain deliveries to your company; downstream covers distribution to your customers. *Unspecified means the available shipment data did not include enough detail to determine a transportation mode (truck, ship, rail, or air). It does not indicate an error in the emissions calculation, only that shipment-level mode detail was not available for those transactions."
  });

  return [pages, shippingModeTransactionsLoading];
};

const FacilitiesTableRow = ({ name, ...otherProps }) => (
  <TableRow>
    <BrandedTableCell>{name}</BrandedTableCell>
    {Object.values(otherProps).map((value, idx) => (
      <BrandedTableCell key={`facility-row-cell-${idx}`}>
        {formatDecimal(value)}
      </BrandedTableCell>
    ))}
  </TableRow>
);

const useFacilitiesSummaryTable = ({ startDate, endDate }) => {
  const [offices, officesLoading] = useOfficesWithCurrentChanges();
  const {
    separateUtilitiesOfficesDefaultEmissions,
    monthlyUtilitiesEmissionsTons,
    utilitiesTransactions,
    emissionsLoading
  } = useEmissionsContext();

  const periodFilteredOffices = filterOfficesByPeriod({
    offices,
    startDate,
    endDate
  });

  // `defaults`/`recurring` are calcs/recurring output (local-anchored,
  // self-consistent under bare dayjs() per #2119). `transactions`, unlike
  // every other array mixed into this file, is the RAW `transactions`
  // collection (useEmissionsContext's utilitiesTransactions), not an
  // aggregated month-rollup doc -- it never passes through
  // correctDateForAggregation / reshapeRollupsV2MonthDocsToLegacyShape, so
  // it does not necessarily carry their +1-day margin. At least the CSV
  // utility-upload path (controllers/transactions.js's sortUtilities,
  // parseFlexibleDate) writes an exact UTC-midnight Timestamp with no
  // margin, via prepareFirebaseObj's isDateKey coercion running in the
  // (UTC) Cloud Functions runtime -- a bare local read of that in a
  // negative-UTC-offset viewer can roll a day-1-of-month entry back into
  // the previous day, misfiling it a month (or year) early in a report.
  // Flagged as a known gap, not fixed here -- narrow (day-1 CSV-uploaded
  // utility entries only) and pre-existing, unrelated to tonight's
  // recurring/#2119 correction.
  const gatherOfficeElectricityEmissions = (officeId, subcategory) => {
    const defaults = separateUtilitiesOfficesDefaultEmissions.filter(
      ({ taggedOfficeId, subcategory: subcat }) =>
        taggedOfficeId === officeId && subcat === subcategory
    );

    const recurring = monthlyUtilitiesEmissionsTons.filter(
      ({ office, subcategory: subcat }) =>
        office?.id === officeId && subcat === subcategory
    );

    const transactions = utilitiesTransactions.filter(
      ({ office, subcategory: subcat }) =>
        office?.id === officeId && subcat === subcategory
    );

    return [...defaults, ...recurring, ...transactions].filter(({ date }) =>
      filterByDateRange({
        filterStartDate: startDate,
        filterEndDate: endDate,
        date,
        unit: "day"
      })
    );
  };

  const buildTableData = () => {
    if (!periodFilteredOffices.length) {
      return [];
    }

    const officeFormattedEmissions = periodFilteredOffices.map(
      ({ name, id }) => {
        const electricityEmissions = gatherOfficeElectricityEmissions(
          id,
          "electricity"
        );
        const leasedEmissions = gatherOfficeElectricityEmissions(
          id,
          "upstream-leased-assets"
        );

        const electricityTonsCo2e = sumTonsCo2e(electricityEmissions);
        const leasedElectricityTonsCo2e = sumOfficeEmissionsProperty(
          leasedEmissions,
          "electricCarbonTons"
        );
        const totalEmittedTonsCo2e =
          electricityTonsCo2e + leasedElectricityTonsCo2e;

        const recsTons = sumOfficeEmissionsProperty(
          electricityEmissions,
          "renewableCarbonTonsCo2e"
        );

        const totalTonsCo2e = totalEmittedTonsCo2e + recsTons;

        const totalEmissionsKwh = sumOfficeEmissionsProperty(
          [...electricityEmissions, ...leasedEmissions],
          "electricKwh",
          "totalKwh"
        );

        const totalEmissionsRecsKwh = sumOfficeEmissionsProperty(
          electricityEmissions,
          "recsKwh"
        );

        return {
          name,
          totalEmissionsKwh,
          totalTonsCo2e,
          totalEmissionsRecsKwh,
          adjustedTonsCo2e: totalEmittedTonsCo2e
        };
      }
    );

    const totalsRowValues = Object.fromEntries(
      [
        "totalEmissionsKwh",
        "totalTonsCo2e",
        "totalEmissionsRecsKwh",
        "adjustedTonsCo2e"
      ].map((property) => {
        const totalValue = officeFormattedEmissions.reduce(
          (sum, office) => sum + Number(office[property] || 0),
          0
        );
        return [property, totalValue];
      })
    );

    const totalsRow = { name: "Totals", ...totalsRowValues };

    return [...officeFormattedEmissions, totalsRow];
  };

  return useWrappedTableRows({
    rows: buildTableData(),
    columns: [
      "Facility Name",
      "kWh Total",
      "Location Based Total tCO2e",
      "Market Based REC kWh",
      "Market Based Adjusted tCO2e"
    ],
    title: "Electricity Use and Emissions By Facility",
    RowComponent: FacilitiesTableRow,
    headerText:
      "This table presents each facility’s electricity use and emissions using both location-based and market-based methods, tracking energy use and renewable energy procurement. Directly-purchased electricity is reported as Scope 2; electricity for facilities whose utilities are included in the lease is reported as Scope 3 (upstream leased assets).",
    isLoading: officesLoading || emissionsLoading
  });
};

const NameCell = ({ scopeIdx, name, rowSpan }) => {
  return (
    <>
      {scopeIdx === 0 && (
        <BrandedTableCell rowSpan={rowSpan}>{name}</BrandedTableCell>
      )}
    </>
  );
};

const FacilitiesDetailsTableRow = ({
  name,
  emissions,
  totalTons,
  totalPercentage
}) => {
  const totalSubcategoryRows = emissions.reduce(
    (sum, { subcategoryEmissions }) => sum + subcategoryEmissions.length,
    0
  );

  const totalsRow = [
    "Location Total",
    formatDecimal(totalTons),
    `${formatDecimal(totalPercentage * 100)}%`
  ];

  return (
    <>
      {emissions.map(({ scope, subcategoryEmissions }, scopeIdx) => {
        return subcategoryEmissions.map(
          ({ subcategory, totalTons, percentage, usage }, subcategoryIdx) => {
            return (
              <TableRow key={`${scope}-${subcategory}`}>
                <NameCell
                  scopeIdx={scopeIdx}
                  name={name}
                  rowSpan={totalSubcategoryRows}
                />
                {!subcategoryIdx && (
                  <BrandedTableCell rowSpan={subcategoryEmissions.length}>
                    Scope {scope}
                  </BrandedTableCell>
                )}
                <BrandedTableCell>
                  {ucFirstLetters(subcategory)}
                </BrandedTableCell>
                <BrandedTableCell>{usage}</BrandedTableCell>
                <BrandedTableCell>{formatDecimal(totalTons)}</BrandedTableCell>
                <BrandedTableCell>
                  {formatDecimal(percentage * 100)}%
                </BrandedTableCell>
              </TableRow>
            );
          }
        );
      })}
      <TableRow>
        {totalsRow.map((value, idx) => (
          <BrandedTableCell
            key={`totals-row-cell-${idx}`}
            colSpan={!idx ? 4 : 1}
          >
            <strong>{value}</strong>
          </BrandedTableCell>
        ))}
      </TableRow>
    </>
  );
};

const useWrappedFacilitiesDetailsTable = ({
  offices,
  emissions,
  totalReportPeriodTons,
  startDate,
  endDate,
  isYearLocked
}) => {
  const formattedTableData = offices.map(
    ({ emissions: officeEmissions, name }) => {
      // Unlike gatherOfficeElectricityEmissions above, `officeEmissions`
      // here is helpers/components/offices.js's formatDisplayOffices
      // output -- calcs/recurring entries (local-anchored) plus
      // splitAggregatedTransactionsBreakdownSubcategories'
      // margined-transaction-month entries, never the raw `transactions`
      // collection. Safe with bare filterByDateRange per #2119's model.
      // buildFacilityInventoryEmissions drops the synthetic avoided-emission
      // rows and splits combined utilities so these scope rows tie to the
      // Scope pages (see buildFacilityScopeReconciliation).
      const dateFilteredEmissions = filterEmissionsToReportPeriod(
        buildFacilityInventoryEmissions(officeEmissions),
        startDate,
        endDate
      );

      const emissionsByScope = scopesList.map(({ name: scope }) => ({
        scope,
        scopeEmissions: filterEmissionsByScope(dateFilteredEmissions, scope)
      }));

      const emissionsBySubcategory = emissionsByScope.map(
        ({ scope, scopeEmissions }) => {
          if (!scopeEmissions.length) {
            return {
              scope,
              subcategoryEmissions: [
                {
                  subcategory: scope === 1 ? "gas" : "electricity",
                  totalTons: 0,
                  percentage: 0,
                  usage: "—"
                }
              ]
            };
          }

          const uniqueSubcategories = [
            ...new Set(scopeEmissions.map(({ subcategory }) => subcategory))
          ];

          const subcategoryEmissions = uniqueSubcategories.map(
            (subcategory) => {
              const emissionsBySubcategory = filterEmissionsBySubcategory(
                dateFilteredEmissions,
                subcategory
              );
              const totalTons = sumTonsCo2e(emissionsBySubcategory);

              const percentage = totalTons / totalReportPeriodTons;

              return {
                subcategory,
                totalTons,
                percentage,
                usage: buildSubcategoryUsage(subcategory, emissionsBySubcategory)
              };
            }
          );

          return { scope, subcategoryEmissions };
        }
      );

      const officeTotalTons = sumTonsCo2e(
        emissionsByScope.flatMap(({ scopeEmissions }) => scopeEmissions)
      );
      return {
        name,
        emissions: emissionsBySubcategory,
        totalTons: officeTotalTons,
        totalPercentage: officeTotalTons / totalReportPeriodTons
      };
    }
  );

  return useWrappedTableRows({
    rows: formattedTableData,
    columns: [
      "Facility Name",
      "Scope",
      "Activity Type",
      "Usage",
      "tCO2e",
      "% of Total Inventory"
    ],
    title: "Facility Scope Emissions Summary",
    RowComponent: FacilitiesDetailsTableRow,
    headerText: `This report provides a summary of greenhouse gas emissions by facility, categorized by scope and activity type. It tracks each facility’s contribution to the total emissions inventory and highlights key sources of emissions. ${buildFacilityReconciliationText(
      {
        reconciliation: buildFacilityScopeReconciliation({
          offices,
          emissions,
          startDate,
          endDate
        }),
        isYearLocked
      }
    )}`
  });
};

const FacilityBoundaryTableRow = ({
  name,
  address,
  facilityType,
  squareFootage,
  utilities
}) => (
  <TableRow>
    <BrandedTableCell>{name}</BrandedTableCell>
    <BrandedTableCell>{address}</BrandedTableCell>
    <BrandedTableCell>{facilityType}</BrandedTableCell>
    <BrandedTableCell>{squareFootage}</BrandedTableCell>
    <BrandedTableCell>{utilities}</BrandedTableCell>
  </TableRow>
);

// Driven by the facilities on record rather than by facilities that produced emissions, so a
// facility entered without any utility data still appears. The point of the table is to show what
// the boundary contains, which includes showing what it doesn't.
const useFacilityBoundaryTable = ({
  offices,
  officesLoading,
  startDate,
  endDate
}) => {
  const { employees, emissionsLoading } = useEmissionsContext();

  return useWrappedTableRows({
    rows: buildFacilityBoundaryRows({
      offices,
      employees,
      isLoading: officesLoading || emissionsLoading,
      startDate,
      endDate
    }),
    columns: [
      "Facility Name",
      "Address",
      "Facility Type",
      "Square Footage",
      "Utilities"
    ],
    title: "Facility Boundary",
    RowComponent: FacilityBoundaryTableRow,
    headerText:
      "This table lists the facilities included in the boundary of this inventory, with the address and square footage of each. Every facility on record for the reporting period is listed, including any that produced no emissions data of its own. Employee home offices are part of the company\u2019s footprint and are reported here as an aggregate count and total area; individual employee addresses are not disclosed."
  });
};

const DisclaimersPage = ({ companyName }) => (
  <Grid container spacing={4}>
    <Grid item>
      <PageHeaderText title="Methodology">
        Emissions in this report are calculated using Aclymate’s carbon
        accounting software, applying the greenhouse gas accounting methodology,
        emission factors, and category definitions of the GHG Protocol Corporate
        Accounting and Reporting Standard and the Corporate Value Chain (Scope
        3) Standard. Emissions are reported in metric tonnes of carbon dioxide
        equivalent (tCO2e). Scope 2 emissions are reported using both
        location-based and market-based methods.
      </PageHeaderText>
    </Grid>
    <Grid item>
      <PageHeaderText title="Inventory Boundary and Completeness">
        This report covers the facilities and activities listed in the Facility
        Emissions Summary. Scope 3 emissions are reported by category in the
        Scope 3 Breakdown, which states for each of the fifteen GHG Protocol
        Scope 3 categories whether it was calculated or not calculated.
        Categories marked “not calculated” were not evaluated for this reporting
        period and are excluded from the totals in this report. All calculations
        depend on the completeness and accuracy of the input data supplied by{" "}
        {companyName} and its vendors.
      </PageHeaderText>
    </Grid>
    <Grid item>
      <PageHeaderText title="Disclaimers">
        This report has not been independently verified or audited by a third
        party. This report is intended for informational and planning purposes
        only. It should not be used as a definitive statement of legal
        compliance, regulatory reporting, or investment advice without
        additional expert review.
      </PageHeaderText>
    </Grid>
    <Grid item>
      <GrayPaper>
        <Grid container spacing={4} direction="column">
          <Grid item>
            <Typography variant="body1" align="center">
              This report was prepared by <strong>Aclymate</strong>,{" "}
              {companyName}&apos;s trusted partner in measuring, reducing, and
              reporting carbon emissions.
            </Typography>
          </Grid>
          <Grid item container spacing={4} alignItems="stretch">
            <Grid item xs={5}>
              <DefaultPaper
                style={{ height: "250px" }}
                boxStyle={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <img
                  alt="Aclymate Logo"
                  src="/images/aclymate-vertical-logo.png"
                  style={{ width: "80%" }}
                />
              </DefaultPaper>
            </Grid>
            <Grid item xs={7}>
              <Box
                style={{ height: "100%" }}
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
              >
                <Typography variant="body1">
                  If you have any questions about this emissions data, need
                  assistance with regulatory compliance, or want to explore our
                  sustainability solutions, please contact us.
                </Typography>
                <Grid container direction="column" spacing={1}>
                  {[
                    { icon: faGlobe, text: "aclymate.com" },
                    { icon: faEnvelope, text: "info@aclymate.com" },
                    { icon: faPhone, text: "+1 (303) 390-1248" }
                  ].map(({ icon, text }) => (
                    <Grid key={text} item container spacing={2} wrap="nowrap">
                      <Grid item>
                        <FontAwesomeIcon icon={icon} />
                      </Grid>
                      <Grid item>
                        <Typography variant="body1">{text}</Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </GrayPaper>
    </Grid>
  </Grid>
);

// A logical report section (e.g. one Scope's page) can dynamically wrap into
// more than one physical page once its content is measured, so page
// numbers/totals can't be derived from the logical section count alone —
// they're built from each section's actual reported physical page count
// (physicalPageCounts[idx], defaulting to 1 for sections not yet measured).
const buildReportPageNumbering = (entryCount, physicalPageCounts) => {
  const physicalPageCountsByIdx = Array.from(
    { length: entryCount },
    (_entry, idx) => physicalPageCounts[idx] ?? 1
  );

  const pageNumberOffsets = physicalPageCountsByIdx.reduce(
    (offsets, _count, idx) => [
      ...offsets,
      idx === 0 ? 0 : offsets[idx - 1] + physicalPageCountsByIdx[idx - 1]
    ],
    []
  );

  const totalPages = physicalPageCountsByIdx.reduce(
    (sum, count) => sum + count,
    0
  );

  return { pageNumberOffsets, totalPages };
};

const ReportPages = ({
  startDate,
  endDate,
  emissions: rawEmissions,
  graphPeriod,
  pdfLoading
}) => {
  const [{ name: companyName }, accountDataLoading] = useAccountData();
  const { offices, officesLoading } = useOfficesData();
  const [accountingYears] = useCachedDisplayData("accounting");
  const {
    utilitiesTransactions = [],
    monthlyUtilitiesEmissionsTons = [],
    separateUtilitiesOfficesDefaultEmissions = []
  } = useEmissionsContext() || {};

  const emissions = useMemo(
    () => expandUtilitiesEmissions(rawEmissions),
    [rawEmissions]
  );

  // Closed years' usage comes from the snapshot entries already in
  // `emissions`; only open months are read from the raw utility rows.
  const electricityUsage = buildElectricityUsageSummary({
    emissions: filterEmissionsByScope(emissions, 2),
    openPeriodUsageSources: filterEntriesOutsideClosedYears(
      filterEmissionsToReportPeriod(
        [
          ...utilitiesTransactions,
          ...monthlyUtilitiesEmissionsTons,
          ...separateUtilitiesOfficesDefaultEmissions
        ],
        startDate,
        endDate
      ),
      buildClosedYearsSet(accountingYears)
    )
  });

  const periodFilteredOffices = filterOfficesByPeriod({
    offices,
    startDate,
    endDate
  });

  const totalReportPeriodTons = sumTonsCo2e(emissions);

  const scopeThreeCategoriesTables = useScopeThreeCategoriesTables(emissions);
  const [shippingBreakdownPages, shippingModeTransactionsLoading] =
    useShippingBreakdownTable(emissions, { startDate, endDate });

  const offsetsPages = useOffsetsPages(
    graphPeriod,
    Number(dayjs(startDate).year())
  );

  const facilityBoundaryTables = useFacilityBoundaryTable({
    offices: periodFilteredOffices,
    officesLoading,
    startDate,
    endDate
  });

  const facilitiesSummaryTables = useFacilitiesSummaryTable({
    startDate,
    endDate
  });

  const wrappedFacilitiesDetailsTables = useWrappedFacilitiesDetailsTable({
    offices: periodFilteredOffices,
    emissions,
    totalReportPeriodTons,
    startDate,
    endDate,
    isYearLocked: findIsReportYearLocked(accountingYears, startDate)
  });

  const reportPages = [
    { content: <TitlePage startDate={startDate} endDate={endDate} /> },
    {
      content: (
        <EmissionsSummaryPage
          emissions={emissions}
          startDate={startDate}
          endDate={endDate}
          totalReportPeriodTons={totalReportPeriodTons}
          offices={periodFilteredOffices}
          graphPeriod={graphPeriod}
        />
      )
    },
    ...scopesList.map((scope) =>
      buildScopeDisplayBlocks({
        ...scope,
        emissions: filterEmissionsByScope(emissions, scope.name),
        companyName,
        electricityUsage
      })
    ),
    ...offsetsPages,
    ...scopeThreeCategoriesTables,
    ...(findShippingEmissions(emissions).length
      ? shippingModeTransactionsLoading
        ? [{ content: <AnimatedLogo /> }]
        : shippingBreakdownPages
      : []),
    ...(periodFilteredOffices.length ? facilityBoundaryTables : []),
    ...(periodFilteredOffices.length ? facilitiesSummaryTables : []),
    ...(periodFilteredOffices.length ? wrappedFacilitiesDetailsTables : []),
    { content: <DisclaimersPage companyName={companyName} /> }
  ];

  const filteredReportPages = reportPages.filter(Boolean);

  const [physicalPageCounts, setPhysicalPageCounts] = useState({});

  const handlePhysicalPageCountChange = useCallback((idx, count) => {
    setPhysicalPageCounts((prevCounts) =>
      prevCounts[idx] === count ? prevCounts : { ...prevCounts, [idx]: count }
    );
  }, []);

  const { pageNumberOffsets, totalPages } = buildReportPageNumbering(
    filteredReportPages.length,
    physicalPageCounts
  );

  return (
    <>
      {!(accountDataLoading || officesLoading) &&
        filteredReportPages.map((pageRow, idx) => (
          <ReportPage
            key={`report-page-${idx}`}
            startDate={startDate}
            endDate={endDate}
            showHeader={!!idx}
            pdfLoading={pdfLoading}
            pageNumberOffset={pageNumberOffsets[idx]}
            totalPages={totalPages}
            onPhysicalPageCountChange={(count) =>
              handlePhysicalPageCountChange(idx, count)
            }
            {...pageRow}
          />
        ))}
    </>
  );
};

const Report = ({ setShowReport, startDate, endDate, ...otherProps }) => {
  const [{ name: companyName = "" }, companyDataLoading] = useAccountData();
  const [accountingYears] = useCachedDisplayData("accounting");

  const isFinalized = findIsReportYearLocked(accountingYears, startDate);

  if (companyDataLoading) {
    return <></>;
  }

  return (
    <ReportLayout
      setShowReport={setShowReport}
      ReportComponent={ReportPages}
      initialName={`${companyName} ${formatDate(startDate)} - ${formatDate(
        endDate
      )} Emissions`}
      reportProps={{ startDate, endDate, ...otherProps }}
      isFinalized={isFinalized}
    />
  );
};
export {
  ReportPages,
  buildFacilityInventoryEmissions,
  buildFacilityScopeReconciliation,
  buildFacilityReconciliationText,
  findIsReportYearLocked,
  extractEmissionProperty,
  gasCubicFeet,
  buildSubcategoryUsage,
  computeUnspecifiedShippingTons,
  buildOffsetProjectKey,
  dedupeOffsetsByProject,
  findAttributionYear,
  buildReportPageNumbering,
  calculateElectricityLocationAndMarketTons,
  buildElectricityUsageSummary,
  buildScopeDisplayBlocks,
  LocationMarketComparisonBar,
  useWrappedTableRows
};
export default Report;
